/**
 * Authentication Context for Briefcase AI Telemetry Dashboard
 *
 * Provides global authentication state management with JWT token handling,
 * automatic refresh, session management, and organization context.
 *
 * Features:
 * - JWT token management with automatic refresh
 * - Secure token storage (memory + httpOnly cookies)
 * - Organization context management
 * - Permission-based access control
 * - Session timeout handling
 * - Multi-factor authentication support
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User, Organization, Permission, AuthTokens } from '../../../contracts/types';
import { apiClient } from './api-client';
import { auditService } from './audit-service';
import { EnhancedUser } from './rbac-types';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: Permission[];
  sessionId: string | null;
  lastActivity: Date | null;
  securityLevel: 'normal' | 'elevated' | 'high';
}

interface AuthContextType extends AuthState {
  // Authentication methods
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;

  // Permission methods
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;

  // Organization methods
  switchOrganization: (orgId: string) => Promise<void>;
  getOrganizationContext: () => Organization | null;

  // Session methods
  updateActivity: () => void;
  checkSession: () => Promise<boolean>;
  getSessionInfo: () => { sessionId: string | null; lastActivity: Date | null };

  // Security methods
  requireMFA: () => Promise<boolean>;
  updateSecurityLevel: (level: 'normal' | 'elevated' | 'high') => void;
}

// API client for authentication operations
class AuthAPI {
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'https://api.briefcasebrain.com';
  }

  async login(email: string, password: string, mfaCode?: string): Promise<any> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web-dashboard'
      },
      credentials: 'include', // Include cookies for refresh token
      body: JSON.stringify({ email, password, mfaCode })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Login failed');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    }
  }

  async refreshToken(): Promise<any> {
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    return response.json();
  }

  async validateSession(): Promise<any> {
    const response = await fetch(`${this.baseURL}/auth/me`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web-dashboard'
      }
    });

    if (!response.ok) {
      throw new Error('Session validation failed');
    }

    return response.json();
  }

  async switchOrganization(orgId: string): Promise<any> {
    const response = await fetch(`${this.baseURL}/auth/switch-organization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ orgId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Organization switch failed');
    }

    return response.json();
  }
}

const authAPI = new AuthAPI();

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    organization: null,
    isAuthenticated: false,
    isLoading: true,
    permissions: [],
    sessionId: null,
    lastActivity: null,
    securityLevel: 'normal'
  });

  // Refs for token management
  const accessTokenRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityTimerRef = useRef<NodeJS.Timeout | null>(null);


  /**
   * Update activity timestamp and extend session
   */
  const updateActivity = useCallback(() => {
    setAuthState(prev => ({ ...prev, lastActivity: new Date() }));

    // Reset activity timer
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }

    // Auto-logout after 30 minutes of inactivity
    activityTimerRef.current = setTimeout(() => {
      logout();
    }, 30 * 60 * 1000);
  }, []);

  /**
   * Schedule automatic token refresh
   */
  const scheduleTokenRefresh = useCallback((accessToken: string) => {
    try {
      // Parse JWT payload to get expiry time
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const refreshTime = expiryTime - Date.now() - 60000; // Refresh 1 minute before expiry

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = setTimeout(async () => {
        try {
          const success = await refreshToken();
          if (!success) {
            await logout();
          }
        } catch (error) {
          console.error('Automatic token refresh failed:', error);
          await logout();
        }
      }, Math.max(refreshTime, 0));
    } catch (error) {
      console.error('Failed to schedule token refresh:', error);
    }
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await authAPI.login(email, password, mfaCode);
      const { access_token, user } = response.data;

      // Store access token in memory and set it in API client
      accessTokenRef.current = access_token;
      apiClient.setAccessToken(access_token);

      // Update auth state
      setAuthState({
        user: user,
        organization: user.organization,
        isAuthenticated: true,
        isLoading: false,
        permissions: user.permissions || [],
        sessionId: user.sessionId,
        lastActivity: new Date(),
        securityLevel: 'normal'
      });

      // Set audit context
      auditService.setUserContext(user as EnhancedUser, user.organization?.org_id);
      await auditService.logUserLogin(user.user_id, true, {
        email: email,
        login_method: mfaCode ? 'mfa' : 'password',
        session_id: user.sessionId
      });

      // Schedule token refresh
      scheduleTokenRefresh(access_token);

      // Start activity monitoring
      updateActivity();

    } catch (error) {
      // Log failed login attempt
      await auditService.logUserLogin('unknown', false, {
        email: email,
        error: error instanceof Error ? error.message : 'Unknown error',
        attempted_mfa: !!mfaCode
      });

      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        organization: null
      }));
      throw error;
    }
  }, [scheduleTokenRefresh, updateActivity]);

  /**
   * Logout user and clear session
   */
  const logout = useCallback(async () => {
    const currentUserId = authState.user?.user_id;

    try {
      // Log logout before clearing context
      if (currentUserId) {
        await auditService.logUserLogout(currentUserId);
      }

      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Flush any pending audit logs
      await auditService.flush();

      // Clear audit context
      auditService.clearUserContext();

      // Clear timers
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }

      // Clear tokens
      accessTokenRef.current = null;
      apiClient.setAccessToken(null);

      // Reset auth state
      setAuthState({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: [],
        sessionId: null,
        lastActivity: null,
        securityLevel: 'normal'
      });
    }
  }, [authState.user]);

  /**
   * Refresh access token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authAPI.refreshToken();
      const { access_token } = response.data;

      // Update access token
      accessTokenRef.current = access_token;
      apiClient.setAccessToken(access_token);

      // Schedule next refresh
      scheduleTokenRefresh(access_token);

      // Update activity
      updateActivity();

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [scheduleTokenRefresh, updateActivity]);

  /**
   * Check if session is still valid
   */
  const checkSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authAPI.validateSession();
      return response.success;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }, []);

  /**
   * Switch to different organization
   */
  const switchOrganization = useCallback(async (orgId: string) => {
    try {
      const response = await authAPI.switchOrganization(orgId);
      const { access_token, user } = response.data;

      // Update access token
      accessTokenRef.current = access_token;
      apiClient.setAccessToken(access_token);

      // Update auth state with new organization
      setAuthState(prev => ({
        ...prev,
        user: user,
        organization: user.organization,
        permissions: user.permissions || []
      }));

      // Schedule token refresh
      scheduleTokenRefresh(access_token);

    } catch (error) {
      console.error('Organization switch failed:', error);
      throw error;
    }
  }, [scheduleTokenRefresh]);

  // Permission checking methods
  const hasPermission = useCallback((permission: Permission): boolean => {
    return authState.permissions.includes(permission);
  }, [authState.permissions]);

  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    return permissions.some(permission => authState.permissions.includes(permission));
  }, [authState.permissions]);

  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    return permissions.every(permission => authState.permissions.includes(permission));
  }, [authState.permissions]);

  // Organization methods
  const getOrganizationContext = useCallback((): Organization | null => {
    return authState.organization;
  }, [authState.organization]);

  // Session methods
  const getSessionInfo = useCallback(() => {
    return {
      sessionId: authState.sessionId,
      lastActivity: authState.lastActivity
    };
  }, [authState.sessionId, authState.lastActivity]);

  // Security methods
  const requireMFA = useCallback(async (): Promise<boolean> => {
    // Implementation would depend on MFA setup
    return false;
  }, []);

  const updateSecurityLevel = useCallback((level: 'normal' | 'elevated' | 'high') => {
    setAuthState(prev => ({ ...prev, securityLevel: level }));
  }, []);

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to validate existing session
        const isValid = await checkSession();
        if (isValid) {
          // Refresh token to get current user data
          const success = await refreshToken();
          if (!success) {
            await logout();
          }
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initializeAuth();
  }, [checkSession, refreshToken, logout]);

  // Setup activity listeners
  useEffect(() => {
    if (authState.isAuthenticated) {
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

      const handleActivity = () => updateActivity();

      activityEvents.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });

      return () => {
        activityEvents.forEach(event => {
          document.removeEventListener(event, handleActivity, true);
        });
      };
    }
  }, [authState.isAuthenticated, updateActivity]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, []);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    switchOrganization,
    getOrganizationContext,
    updateActivity,
    checkSession,
    getSessionInfo,
    requireMFA,
    updateSecurityLevel
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to get current access token
 */
export const useAccessToken = (): string | null => {
  const { isAuthenticated } = useAuth();
  // Get token directly from API client which is already set by AuthContext
  return isAuthenticated ? apiClient.getAccessToken() : null;
};

/**
 * Higher-order component for route protection
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions?: Permission[]
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading, hasAllPermissions } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login or show login form
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please log in to access this page.</p>
          </div>
        </div>
      );
    }

    if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
            <p className="text-gray-600">You don't have sufficient permissions to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

/**
 * Permission-based component wrapper
 */
interface PermissionGateProps {
  children: React.ReactNode;
  permissions: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permissions,
  requireAll = true,
  fallback = null
}) => {
  const { hasAllPermissions, hasAnyPermission } = useAuth();

  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default AuthProvider;